import React, { useState } from 'react';
import { AlertTriangle, Camera, Check, Copyright, Mic, Shield, User, X } from 'lucide-react';

const TERMS_VERSION = '1.0.0';

export const LiveTermsModal = ({ isOpen, onClose, onAccept, lang = 'es', isLoading = false }) => {
  const [checks, setChecks] = useState({
    age: false,
    terms: false,
    content: false,
    rights: false
  });

  const allChecked = checks.age && checks.terms && checks.content && checks.rights;

  const toggleCheck = (key) => {
    setChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAccept = () => {
    if (allChecked && onAccept) {
      onAccept({
        accept_live_terms: checks.terms,
        accept_content_policy: checks.content,
        confirm_age_18_plus: checks.age,
        confirm_content_rights: checks.rights
      });
    }
  };

  if (!isOpen) return null;

  const t = {
    title: lang === 'es' ? 'Términos de Emisión en Vivo' : 'Live Streaming Terms',
    subtitle: lang === 'es' 
      ? 'Antes de iniciar tu directo, debes aceptar estos términos' 
      : 'Before starting your live, you must accept these terms',
    
    ageTitle: lang === 'es' ? 'Edad y capacidad legal' : 'Age and legal capacity',
    ageDesc: lang === 'es' 
      ? 'Confirmo que tengo al menos 18 años y capacidad legal para emitir contenido' 
      : 'I confirm I am at least 18 years old and have legal capacity to broadcast content',
    
    rightsTitle: lang === 'es' ? 'Derechos de contenido' : 'Content rights',
    rightsDesc: lang === 'es' 
      ? 'Soy propietario o tengo licencia de todo el contenido que transmitiré, incluyendo música, imágenes y videos' 
      : 'I own or have license for all content I will broadcast, including music, images and videos',
    
    contentTitle: lang === 'es' ? 'Política de contenido' : 'Content policy',
    contentDesc: lang === 'es' 
      ? 'Entiendo que está prohibido transmitir: desnudos, violencia, discurso de odio, actividad ilegal o contenido con menores' 
      : 'I understand broadcasting is prohibited: nudity, violence, hate speech, illegal activity or content with minors',
    
    termsTitle: lang === 'es' ? 'Términos del servicio' : 'Terms of service',
    termsDesc: lang === 'es' 
      ? 'Acepto los Términos de Uso y la Política de Privacidad de PulseMusic. Entiendo que el 30% de las propinas es comisión de plataforma' 
      : 'I accept PulseMusic Terms of Use and Privacy Policy. I understand 30% of tips is platform commission',
    
    cameraNote: lang === 'es' 
      ? 'Al iniciar, se solicitará acceso a tu cámara y micrófono' 
      : 'When starting, camera and microphone access will be requested',
    
    accept: lang === 'es' ? 'Aceptar y continuar' : 'Accept and continue',
    cancel: lang === 'es' ? 'Cancelar' : 'Cancel',
    version: `v${TERMS_VERSION}`
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div 
        data-testid="live-terms-modal"
        className="relative w-full max-w-lg bg-[#0f0f0f] rounded-3xl border border-white/10 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#d4a853]/10 border border-[#d4a853]/20 flex items-center justify-center">
                <Shield size={20} className="text-[#d4a853]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{t.title}</h2>
                <p className="text-xs text-white/40">{t.version}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
            >
              <X size={16} className="text-white/60" />
            </button>
          </div>
          <p className="text-sm text-white/60">{t.subtitle}</p>
        </div>

        {/* Terms checkboxes */}
        <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
          {/* Age verification */}
          <label 
            data-testid="live-terms-age-check"
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition ${
              checks.age 
                ? 'bg-[#d4a853]/5 border-[#d4a853]/30' 
                : 'bg-white/5 border-white/10 hover:border-white/20'
            }`}
          >
            <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition mt-0.5 ${
              checks.age ? 'bg-[#d4a853] border-[#d4a853]' : 'border-white/30'
            }`}>
              {checks.age && <Check size={12} className="text-black" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <User size={14} className="text-[#d4a853]" />
                <span className="text-sm font-medium text-white">{t.ageTitle}</span>
              </div>
              <p className="text-xs text-white/50 leading-relaxed">{t.ageDesc}</p>
            </div>
            <input 
              type="checkbox" 
              checked={checks.age} 
              onChange={() => toggleCheck('age')}
              className="sr-only"
            />
          </label>

          {/* Content rights */}
          <label 
            data-testid="live-terms-rights-check"
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition ${
              checks.rights 
                ? 'bg-[#d4a853]/5 border-[#d4a853]/30' 
                : 'bg-white/5 border-white/10 hover:border-white/20'
            }`}
          >
            <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition mt-0.5 ${
              checks.rights ? 'bg-[#d4a853] border-[#d4a853]' : 'border-white/30'
            }`}>
              {checks.rights && <Check size={12} className="text-black" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Copyright size={14} className="text-[#d4a853]" />
                <span className="text-sm font-medium text-white">{t.rightsTitle}</span>
              </div>
              <p className="text-xs text-white/50 leading-relaxed">{t.rightsDesc}</p>
            </div>
            <input 
              type="checkbox" 
              checked={checks.rights} 
              onChange={() => toggleCheck('rights')}
              className="sr-only"
            />
          </label>

          {/* Content policy */}
          <label 
            data-testid="live-terms-content-check"
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition ${
              checks.content 
                ? 'bg-[#d4a853]/5 border-[#d4a853]/30' 
                : 'bg-white/5 border-white/10 hover:border-white/20'
            }`}
          >
            <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition mt-0.5 ${
              checks.content ? 'bg-[#d4a853] border-[#d4a853]' : 'border-white/30'
            }`}>
              {checks.content && <Check size={12} className="text-black" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={14} className="text-[#d4a853]" />
                <span className="text-sm font-medium text-white">{t.contentTitle}</span>
              </div>
              <p className="text-xs text-white/50 leading-relaxed">{t.contentDesc}</p>
            </div>
            <input 
              type="checkbox" 
              checked={checks.content} 
              onChange={() => toggleCheck('content')}
              className="sr-only"
            />
          </label>

          {/* Terms of service */}
          <label 
            data-testid="live-terms-tos-check"
            className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition ${
              checks.terms 
                ? 'bg-[#d4a853]/5 border-[#d4a853]/30' 
                : 'bg-white/5 border-white/10 hover:border-white/20'
            }`}
          >
            <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition mt-0.5 ${
              checks.terms ? 'bg-[#d4a853] border-[#d4a853]' : 'border-white/30'
            }`}>
              {checks.terms && <Check size={12} className="text-black" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Shield size={14} className="text-[#d4a853]" />
                <span className="text-sm font-medium text-white">{t.termsTitle}</span>
              </div>
              <p className="text-xs text-white/50 leading-relaxed">{t.termsDesc}</p>
            </div>
            <input 
              type="checkbox" 
              checked={checks.terms} 
              onChange={() => toggleCheck('terms')}
              className="sr-only"
            />
          </label>
        </div>

        {/* Camera/mic note */}
        <div className="px-6 py-3 bg-white/5 border-t border-white/10">
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Camera size={12} />
            <Mic size={12} />
            <span>{t.cameraNote}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-4 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-sm text-white/60 hover:bg-white/5 transition"
          >
            {t.cancel}
          </button>
          <button
            data-testid="live-terms-accept-button"
            onClick={handleAccept}
            disabled={!allChecked || isLoading}
            className={`flex-1 py-3 rounded-xl text-sm font-medium transition ${
              allChecked && !isLoading
                ? 'bg-[#d4a853] text-black hover:bg-[#c49843]'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              </span>
            ) : (
              t.accept
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
